import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Space,
  Typography,
  Image,
  Popconfirm,
  message,
  Spin
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { gamesService } from '../services/gamesService';

const { Title } = Typography;
const { Option } = Select;

const Games = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadGames();
  }, []);

  const loadGames = async () => {
    try {
      setLoading(true);
      const gamesData = await gamesService.getAllGames();
      setGames(gamesData);
    } catch (error) {
      console.error('Error loading games:', error);
      message.error('Ошибка загрузки игр');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Игра',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Image
            width={40}
            height={40}
            src={record.image}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.description}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      render: (category) => <Tag color="blue">{category}</Tag>
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Активна' : 'Неактивна'}
        </Tag>
      )
    },
    {
      title: 'Игроки',
      dataIndex: 'players',
      key: 'players',
      render: (players) => `${players.toLocaleString()}`
    },
    {
      title: 'Рейтинг',
      dataIndex: 'rating',
      key: 'rating',
      render: (rating) => `${rating} ⭐`
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Удалить игру?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingGame(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    form.setFieldsValue(game);
    setIsModalVisible(true);
  };

  const handleView = (game) => {
    Modal.info({
      title: game.name,
      content: (
        <div>
          <p><strong>Описание:</strong> {game.description}</p>
          <p><strong>Категория:</strong> {game.category}</p>
          <p><strong>Статус:</strong> {game.status === 'active' ? 'Активна' : 'Неактивна'}</p>
          <p><strong>Игроки:</strong> {game.players.toLocaleString()}</p>
          <p><strong>Рейтинг:</strong> {game.rating} ⭐</p>
        </div>
      ),
    });
  };

  const handleDelete = async (id) => {
    try {
      await gamesService.deleteGame(id);
      setGames(games.filter(game => game.id !== id));
      message.success('Игра удалена');
    } catch (error) {
      console.error('Error deleting game:', error);
      message.error('Ошибка удаления игры');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingGame) {
        await gamesService.updateGame(editingGame.id, values);
        await loadGames();
        message.success('Игра обновлена');
      } else {
        await gamesService.createGame(values);
        await loadGames();
        message.success('Игра добавлена');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving game:', error);
      message.error('Ошибка сохранения игры');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  return (
    <div>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Управление играми</Title>
          <p style={{ color: '#666', margin: 0 }}>Добавляйте и редактируйте образовательные игры</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Добавить игру
        </Button>
      </div>

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>Загрузка игр...</div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={games}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} игр`
            }}
          />
        )}
      </Card>

      <Modal
        title={editingGame ? 'Редактировать игру' : 'Добавить игру'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Название игры"
            rules={[{ required: true, message: 'Введите название игры' }]}
          >
            <Input placeholder="Например: 2048" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: 'Введите описание' }]}
          >
            <Input.TextArea rows={3} placeholder="Краткое описание игры" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Категория"
            rules={[{ required: true, message: 'Выберите категорию' }]}
          >
            <Select placeholder="Выберите категорию">
              <Option value="Логика">Логика</Option>
              <Option value="Внимание">Внимание</Option>
              <Option value="Память">Память</Option>
              <Option value="Творчество">Творчество</Option>
              <Option value="Математика">Математика</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 'active' : 'inactive'}
            getValueProps={(value) => ({ checked: value === 'active' })}
          >
            <Switch checkedChildren="Активна" unCheckedChildren="Неактивна" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Games;
